import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useState } from 'react';
import { MapPin, Building2, Grid3x3, Map } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import ListaEstandar from '../ui/ListaEstandar';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import Ayuda from '../ui/Ayuda';
import { textoDeAyuda } from '../ayudas/notas';
import ModalConfirmar from '../ui/ModalConfirmar';
import ChipEstado from '../ui/ChipEstado';
import MapaSucursal from '../ui/MapaSucursal';
import { direccionLegible, tieneCoordenadas, faltaPar } from '../ui/direcciones';
import Modal from '../ui/Modal';
import { Seccion, CampoValidado } from '../ui/Formulario';

// La cadena de almacenamiento va primero y en orden, de lo mayor a lo
// menor: el desplegable enseña la jerarquía sin tener que explicarla.
const TIPOS_ALMACEN = ['ALMACEN', 'PASILLO', 'RACK', 'NIVEL', 'ESTANTE', 'GAVETA'];
// CUARENTENA no es decorativa: de una ubicación de ese tipo el mostrador
// NO vende, y la mercancía se libera con un traspaso (N-15).
const TIPOS_LOGICOS = ['VITRINA', 'TRANSITO', 'CUARENTENA', 'MERMA', 'CLIENTE', 'PROVEEDOR'];
const TIPOS = [...TIPOS_ALMACEN, ...TIPOS_LOGICOS];

// LOS TIPOS YA NO SE ESCRIBEN AQUÍ. Desde la migración 063 viven en la
// base con su nombre en cristiano y su ayuda, y esta lista de arriba se
// queda solo como respaldo para el instante en que la pantalla carga y
// todavía no ha llegado el catálogo. Un desplegable escrito a mano es
// una lista paralela que envejece sola: le faltaban los diez tipos
// nuevos el día de su despliegue.
const GRUPOS = [
  ['ESTRUCTURA', 'Dentro del almacén'],
  ['GUARDA', 'Establecimientos que guardan mercancía'],
  ['NO_GUARDA', 'Sitios que NO guardan mercancía'],
  ['VIRTUAL', 'Ubicaciones lógicas'],
];

function useTipos() {
  const [tipos, setTipos] = useState([]);
  useEffect(() => {
    api.get('/sitios/tipos').then(setTipos).catch(() => setTipos([]));
  }, []);
  return tipos;
}

function SelectorDeTipo({ valor, onChange, tipos, soloAlmacen = false }) {
  // Respaldo mientras carga: mejor un desplegable con lo de siempre que
  // uno vacío.
  if (!tipos.length) {
    const lista = soloAlmacen ? TIPOS_ALMACEN : TIPOS;
    return (
      <select value={valor} onChange={onChange}>
        {lista.map((t) => <option key={t}>{t}</option>)}
      </select>
    );
  }
  const usables = soloAlmacen
    ? tipos.filter((t) => t.grupo === 'ESTRUCTURA')
    : tipos;
  return (
    <select value={valor} onChange={onChange}>
      {GRUPOS.map(([grupo, etiqueta]) => {
        const dentro = usables.filter((t) => t.grupo === grupo);
        if (!dentro.length) return null;
        return (
          <optgroup key={grupo} label={etiqueta}>
            {dentro.map((t) => (
              <option key={t.codigo} value={t.codigo}>{t.nombre}</option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}
const ESTADO_UBIC = {
  activa: { etiqueta: 'Activa', color: 'verde' },
  baja: { etiqueta: 'Dada de baja', color: 'gris' },
};

// El primer paso de la puesta en marcha de cualquier cliente. Hasta el
// cierre del Bloque B esto solo se podía hacer con curl.
export default function Ubicaciones({ irA }) {
  const [pestana, setPestana] = useState('ubicaciones');
  useSubpantalla('pestana', pestana !== 'ubicaciones', () => setPestana('ubicaciones'), { valor: pestana, abrir: setPestana, enUrl: true, reemplaza: true });
  const { tieneNivel } = useAuth();

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Almacén</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Dónde se guarda la mercancía y en qué sucursal está cada cosa.
        </p>
      </div>

      <div className="border-b border-gray-200 flex gap-1 flex-wrap">
        {[
          { id: 'ubicaciones', etiqueta: 'Ubicaciones' },
          ...(tieneNivel('ADMIN') ? [{ id: 'sucursales', etiqueta: 'Sucursales' }] : []),
        ].map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPestana(p.id)}
            aria-current={pestana === p.id ? 'page' : undefined}
            className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${
              pestana === p.id
                ? 'border-[var(--color-marca)] text-[var(--color-marca)]'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {p.etiqueta}
          </button>
        ))}
      </div>

      {pestana === 'ubicaciones' ? <PanelUbicaciones irA={irA} /> : <PanelSucursales />}
    </div>
  );
}

// ------------------------------------------------------------ ubicaciones
function PanelUbicaciones({ irA }) {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);   // {modo, ubicacion}
  const [baja, setBaja] = useState(null);
  const [rejilla, setRejilla] = useState(null); // ubicación contenedora
  const [sucursales, setSucursales] = useState([]);

  const cargar = useCallback(() => {
    setCargando(true);
    return api.get('/ubicaciones?solo_activas=false')
      .then(setLista)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    let vigente = true;
    api.get('/ubicaciones?solo_activas=false')
      .then((d) => vigente && setLista(d))
      .catch((e) => vigente && setError(e.message))
      .finally(() => vigente && setCargando(false));
    // Para poder elegir sucursal al crear una ubicación raíz. Si falla, el
    // formulario sigue sirviendo: la sucursal es opcional.
    api.get('/sucursales').then((d) => vigente && setSucursales(d)).catch(() => {});
    return () => { vigente = false; };
  }, []);

  const columnas = [
    {
      // La lista llega ordenada por ruta desde el backend, así que sangrar
      // por profundidad la convierte en el árbol sin ordenar nada aquí.
      etiqueta: 'Código',
      valor: (u) => u.codigo,
      render: (u) => (
        <span className="font-mono text-xs whitespace-nowrap"
              style={{ paddingLeft: `${(u.profundidad - 1) * 14}px` }}>
          {u.profundidad > 1 && <span className="text-gray-500 mr-1">└</span>}
          {u.codigo}
        </span>
      ),
    },
    { etiqueta: 'Nombre', valor: (u) => u.nombre, clase: 'font-medium text-gray-900' },
    {
      etiqueta: 'Dónde está',
      valor: (u) => u.ruta,
      render: (u) => (
        <span className="text-xs text-gray-500 font-mono" title={u.ruta}>
          {u.ruta.length > 34 ? `…${u.ruta.slice(-33)}` : u.ruta}
        </span>
      ),
    },
    { etiqueta: 'Sucursal', valor: (u) => u.sucursal || '', clase: 'text-gray-500' },
    { etiqueta: 'Tipo', valor: (u) => u.tipo, clase: 'text-gray-500' },
    {
      etiqueta: 'Contiene', alineado: 'derecha',
      valor: (u) => u.hijos,
      render: (u) => (u.hijos
        ? <span className="tabular-nums text-gray-600">{u.hijos}</span>
        : <span className="text-gray-500">—</span>),
    },
    {
      etiqueta: 'Guarda', alineado: 'derecha',
      valor: (u) => Number(u.unidades ?? 0),
      render: (u) => (Number(u.unidades) > 0
        ? <span className="tabular-nums">{Number(u.unidades)} u. en {u.existencias} renglón(es)</span>
        : <span className="text-gray-500">vacía</span>),
    },
    {
      etiqueta: 'Estado', valor: (u) => (u.activo ? 'Activa' : 'Dada de baja'),
      render: (u) => <ChipEstado mapa={ESTADO_UBIC} clave={u.activo ? 'activa' : 'baja'} />,
    },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Ubicaciones"
        subtitulo="Cada sitio donde puede haber mercancía. Es lo primero que hay que dar de alta."
        nota={
          // CAPA 1 · que es esto. Las reglas se fueron a sus controles.
          // La de dar de baja se queda TAMBIEN aqui, en corto: cambia la
          // decision de entrar a la pantalla, no solo la de pulsar.
          <div className="space-y-2">
            <Ayuda id="ubicaciones.pantalla" />
            <Ayuda id="ubicaciones.baja_resumen" />
          </div>
        }
        accionPrincipal={{ etiqueta: 'Nueva ubicación', onClick: () => setEditar({ modo: 'crear' }) }}
        columnas={columnas}
        filas={lista}
        cargando={cargando}
        error={error}
        onReintentar={() => { setError(null); cargar(); }}
        exportar={{ nombre: 'ubicaciones', columnas }}
        vacio={
          <EstadoVacio
            icono={MapPin}
            titulo="Todavía no hay ubicaciones"
            descripcion="Empieza por tu bodega principal. Después puedes colgar de ella los pasillos."
          />
        }
        acciones={(u) => [
          ...(Number(u.unidades) > 0
            ? [{ etiqueta: 'Ver existencias', onClick: () => irA?.('existencias', { ubicacion: u.codigo }) }]
            : []),
          { etiqueta: 'Editar', onClick: () => setEditar({ modo: 'editar', ubicacion: u }) },
          ...(u.activo ? [{ etiqueta: 'Crear rejilla', onClick: () => setRejilla(u) }] : []),
          ...(u.activo
            ? [{ etiqueta: 'Dar de baja', onClick: () => setBaja(u), peligro: true }]
            : [{ etiqueta: 'Reactivar', onClick: () => api.patch(`/ubicaciones/${u.id}`, { activo: true }).then(cargar) }]),
        ]}
      />

      {/* El fallo de carga lo pinta la lista (motivo en español + reintentar). */}

      {editar && (
        <ModalUbicacion
          {...editar}
          ubicaciones={lista.filter((u) => u.activo)}
          sucursales={sucursales}
          onCerrar={() => setEditar(null)}
          onListo={() => { setEditar(null); cargar(); }}
        />
      )}

      {rejilla && (
        <ModalRejilla
          padre={rejilla}
          onCerrar={() => setRejilla(null)}
          onListo={() => { setRejilla(null); cargar(); }}
        />
      )}

      {baja && (
        <ModalConfirmar
          peligro
          titulo={`Dar de baja ${baja.codigo}`}
          descripcion="Deja de ofrecerse al registrar movimientos. El historial de lo que pasó por ella se conserva."
          cambios={[{ campo: 'Estado', antes: 'Activa', despues: 'Dada de baja' }]}
          aviso={Number(baja.unidades) > 0
            ? `Guarda ${Number(baja.unidades)} unidad(es). ${textoDeAyuda('ubicaciones.dar_de_baja')}`
            : null}
          etiquetaConfirmar="Dar de baja"
          onConfirmar={async () => {
            await api.patch(`/ubicaciones/${baja.id}`, { activo: false });
            setBaja(null);
            cargar();
          }}
          onCerrar={() => setBaja(null)}
        />
      )}
    </>
  );
}

function ModalUbicacion({ modo, ubicacion, ubicaciones, sucursales, onCerrar, onListo }) {
  const crear = modo === 'crear';
  const [f, setF] = useState({
    codigo: ubicacion?.codigo || '',
    nombre: ubicacion?.nombre || '',
    tipo: ubicacion?.tipo || 'ALMACEN',
    id_padre: ubicacion?.id_padre ? String(ubicacion.id_padre) : '',
    id_sucursal: ubicacion?.id_sucursal ? String(ubicacion.id_sucursal) : '',
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const tipos = useTipos();

  // Una ubicación no puede colgar de sí misma; el resto de los bucles los
  // detecta el backend, que es quien conoce el árbol entero.
  const posibles = ubicaciones.filter((u) => u.id !== ubicacion?.id);

  return (
    <ModalConfirmar
      titulo={crear ? 'Nueva ubicación' : `Editar ${ubicacion.codigo}`}
      descripcion={crear
        ? 'El código es el que irá impreso en la etiqueta del estante. No se puede cambiar después.'
        : 'El código no se puede cambiar: ya está impreso en etiquetas y en el historial.'}
      extra={
        <div className="space-y-3">
          <Seccion titulo="La ubicación" columnas={1}>
            {crear && (
              <CampoValidado etiqueta="Código" requerido valor={f.codigo}
                             ayuda={textoDeAyuda('ubicaciones.codigo')}
                             validar={(v) => (v.trim() ? null : 'El código es obligatorio.')}>
                {(props) => <input {...props} value={f.codigo} onChange={set('codigo')}
                                   className={`${props.className} font-mono`}
                                   autoCapitalize="characters" placeholder="P3-A" />}
              </CampoValidado>
            )}
            <CampoValidado etiqueta="Nombre" requerido valor={f.nombre}
                           validar={(v) => (v.trim() ? null : 'El nombre es obligatorio.')}>
              {(props) => <input {...props} value={f.nombre} onChange={set('nombre')}
                                 placeholder="Pasillo 3, estante A" />}
            </CampoValidado>
            <CampoValidado etiqueta="Tipo" valor={f.tipo}
                           ayuda={tipos.find((t) => t.codigo === f.tipo)?.ayuda}>
              {(props) => (
                <SelectorDeTipo {...props} valor={f.tipo}
                                onChange={set('tipo')} tipos={tipos} />
              )}
            </CampoValidado>
            <CampoValidado etiqueta="Dentro de" ayuda="Opcional." valor={f.id_padre}>
              {(props) => (
                <select {...props} value={f.id_padre} onChange={set('id_padre')}>
                  <option value="">No está dentro de ninguna</option>
                  {posibles.map((u) => (
                    <option key={u.id} value={u.id}>
                      {'\u00A0'.repeat((u.profundidad - 1) * 3)}{u.codigo} — {u.nombre}
                    </option>
                  ))}
                </select>
              )}
            </CampoValidado>
            {/* La sucursal solo se elige en la raíz: si está dentro de otra,
                hereda la de su contenedora y el backend lo impone. */}
            {!f.id_padre && sucursales?.length > 1 && (
              <CampoValidado etiqueta="Sucursal" valor={f.id_sucursal}>
                {(props) => (
                  <select {...props} value={f.id_sucursal} onChange={set('id_sucursal')}>
                    <option value="">Sin asignar</option>
                    {sucursales.map((s) => (
                      <option key={s.id} value={s.id}>{s.nombre}</option>
                    ))}
                  </select>
                )}
              </CampoValidado>
            )}
          </Seccion>
          {f.id_padre && (
            <p className="text-xs text-gray-500">
              Pertenece a la misma sucursal que la ubicación que la contiene.
            </p>
          )}
        </div>
      }
      etiquetaConfirmar={crear ? 'Crear ubicación' : 'Guardar cambios'}
      onConfirmar={async () => {
        if (crear && !f.codigo.trim()) throw new ErrorApi('El código es obligatorio.');
        if (!f.nombre.trim()) throw new ErrorApi('El nombre es obligatorio.');
        const cuerpo = {
          nombre: f.nombre.trim(),
          tipo: f.tipo,
          ...(f.id_padre ? { id_padre: Number(f.id_padre) } : {}),
        };
        if (crear) {
          await api.post('/ubicaciones', {
            codigo: f.codigo.trim().toUpperCase(),
            ...cuerpo,
            ...(!f.id_padre && f.id_sucursal
              ? { id_sucursal: Number(f.id_sucursal) } : {}),
          });
        } else {
          // Vaciar el desplegable significa "sácala de donde está", que no
          // se distingue de "no la muevas" con un id nulo.
          await api.patch(`/ubicaciones/${ubicacion.id}`,
                          { ...cuerpo, ...(f.id_padre ? {} : { quitar_padre: true }) });
        }
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}

// Alta masiva. Capturar un almacén real a mano son horas: veinte racks por
// cuatro niveles son ochenta formularios idénticos, y es justo el punto
// donde una puesta en marcha se abandona.
function ModalRejilla({ padre, onCerrar, onListo }) {
  const [dims, setDims] = useState([
    { prefijo: 'R', desde: 1, hasta: 10, tipo: 'RACK', digitos: 2 },
  ]);
  const [resultado, setResultado] = useState(null);

  const set = (i, k) => (e) => {
    const v = k === 'prefijo' || k === 'tipo' ? e.target.value : Number(e.target.value);
    setDims((d) => d.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  };

  // Cuántas se van a crear, calculado a la vista: nadie debería descubrir
  // que pidió 3.000 ubicaciones después de pulsar el botón. Cada nivel
  // multiplica al anterior (10 racks x 4 niveles = 10 + 40).
  const anchos = dims.map((d) => Math.max(0, d.hasta - d.desde + 1));
  const total = anchos.reduce(
    (suma, _, i) => suma + anchos.slice(0, i + 1).reduce((a, b) => a * b, 1),
    0,
  );

  const ejemplo = dims.reduce(
    (codigo, d) => `${codigo}-${d.prefijo}${String(d.desde).padStart(d.digitos, '0')}`,
    padre.codigo,
  );

  return (
    <ModalConfirmar
      titulo={`Crear rejilla dentro de ${padre.codigo}`}
      descripcion="Genera de una vez toda una estructura de ubicaciones, con códigos predecibles que luego se imprimen como etiquetas QR en lote."
      extra={
        <div className="space-y-3">
          <Ayuda id="ubicaciones.crear_rejilla" />
          {dims.map((d, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Nivel {i + 1}
                </span>
                {dims.length > 1 && i === dims.length - 1 && (
                  <button onClick={() => setDims((x) => x.slice(0, -1))}
                          className="text-xs font-semibold text-red-600">Quitar</button>
                )}
              </div>
              {/* En un teléfono, cuatro columnas dejan cada campo en 70 px:
                  se ve el borde y no el contenido. */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <CampoValidado etiqueta="Tipo" valor={d.tipo}>
                  {(props) => (
                    <select {...props} value={d.tipo} onChange={set(i, 'tipo')}>
                      {TIPOS_ALMACEN.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  )}
                </CampoValidado>
                <CampoValidado etiqueta="Prefijo" valor={d.prefijo}>
                  {(props) => <input {...props} value={d.prefijo} onChange={set(i, 'prefijo')}
                                     className={`${props.className} font-mono`} />}
                </CampoValidado>
                <CampoValidado etiqueta="Desde" valor={d.desde}
                               validar={() => (Number(d.hasta) < Number(d.desde)
                                 ? 'El rango va al revés: «hasta» es menor que «desde».' : null)}>
                  {(props) => <input {...props} type="number" min={0} value={d.desde}
                                     onChange={set(i, 'desde')} />}
                </CampoValidado>
                <CampoValidado etiqueta="Hasta" valor={d.hasta}
                               validar={() => (Number(d.hasta) < Number(d.desde)
                                 ? 'El rango va al revés: «hasta» es menor que «desde».' : null)}>
                  {(props) => <input {...props} type="number" min={0} value={d.hasta}
                                     onChange={set(i, 'hasta')} />}
                </CampoValidado>
              </div>
            </div>
          ))}

          {dims.length < 3 && (
            <button
              onClick={() => setDims((x) => [...x, { prefijo: 'N', desde: 1, hasta: 4, tipo: 'NIVEL', digitos: 2 }])}
              className="text-sm font-semibold text-[var(--color-marca)]"
            >
              + Añadir un nivel dentro
            </button>
          )}

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
            <div className="text-gray-600">
              Se crearán <strong className="text-gray-900">{total}</strong> ubicaciones.
            </div>
            <div className="text-xs text-gray-500 mt-1">
              La primera se llamará <span className="font-mono">{ejemplo}</span>
            </div>
          </div>

          {resultado && (
            <div className="text-sm bg-green-50 border border-green-200 text-green-800 rounded-lg p-3">
              Creadas {resultado.creadas}
              {resultado.omitidas > 0 && `, omitidas ${resultado.omitidas} que ya existían`}.
            </div>
          )}
        </div>
      }
      aviso={total > 200
        ? `Son ${total} ubicaciones. Conviene comprobar que ese detalle se va a mantener al día.`
        : null}
      etiquetaConfirmar="Crear rejilla"
      onConfirmar={async () => {
        if (total < 1) throw new ErrorApi('Ese rango no crea ninguna ubicación.');
        const r = await api.post('/ubicaciones/rejilla', {
          id_padre: padre.id, dimensiones: dims,
        });
        setResultado(r);
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}

// ------------------------------------------------------------- sucursales
function PanelSucursales() {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);
  const [mapa, setMapa] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    return api.get('/sucursales?solo_activas=false')
      .then(setLista)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    let vigente = true;
    api.get('/sucursales?solo_activas=false')
      .then((d) => vigente && setLista(d))
      .catch((e) => vigente && setError(e.message))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, []);

  const columnas = [
    { etiqueta: 'Nombre', valor: (s) => s.nombre, clase: 'font-medium text-gray-900' },
    {
      etiqueta: 'Dirección',
      valor: (s) => direccionLegible(s),
      render: (s) => (direccionLegible(s)
        ? <span className="text-gray-600">{direccionLegible(s)}</span>
        : <span className="text-gray-500">Sin dirección</span>),
    },
    {
      etiqueta: 'Ubicación', alineado: 'derecha',
      valor: (s) => (tieneCoordenadas(s) ? `${s.latitud},${s.longitud}` : ''),
      render: (s) => (tieneCoordenadas(s)
        ? (
          <button onClick={() => setMapa(s)}
                  className="inline-flex items-center gap-1 text-[var(--color-marca)] font-semibold text-sm">
            <Map className="h-3.5 w-3.5" /> Ver mapa
          </button>
        )
        : <span className="text-gray-500 text-xs">Sin coordenadas</span>),
    },
    {
      etiqueta: 'Almacén', alineado: 'derecha',
      valor: (s) => s.ubicaciones,
      render: (s) => (s.ubicaciones
        ? <span className="tabular-nums text-gray-600">{s.ubicaciones} ubicación(es)</span>
        : <span className="text-gray-500">—</span>),
    },
    {
      etiqueta: 'Estado', valor: (s) => (s.activo ? 'Activa' : 'Dada de baja'),
      render: (s) => <ChipEstado mapa={ESTADO_UBIC} clave={s.activo ? 'activa' : 'baja'} />,
    },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Sucursales"
        subtitulo="Los puntos físicos de tu empresa. Cada empleado pertenece a una."
        nota={
          <NotaExplicativa titulo="Sucursal y ubicación no son lo mismo">
            La <strong>sucursal</strong> es el domicilio: la matriz, la bodega del norte, la tienda del
            centro. La <strong>ubicación</strong> es dónde está la mercancía dentro de ella, y cuelga de
            una sucursal. Tu plan limita cuántas sucursales puedes tener; las ubicaciones no tienen
            límite. Las <strong>coordenadas son opcionales</strong>: sin internet el mapa no carga, pero
            la dirección y las coordenadas se siguen viendo y se pueden copiar.
          </NotaExplicativa>
        }
        accionPrincipal={{ etiqueta: 'Nueva sucursal', onClick: () => setEditar({ modo: 'crear' }) }}
        columnas={columnas}
        filas={lista}
        cargando={cargando}
        error={error}
        onReintentar={() => { setError(null); cargar(); }}
        exportar={{ nombre: 'sucursales', columnas }}
        vacio={<EstadoVacio icono={Building2} titulo="No hay sucursales" descripcion="Da de alta al menos una." />}
        acciones={(s) => [
          { etiqueta: 'Editar', onClick: () => setEditar({ modo: 'editar', sucursal: s }) },
          ...(s.activo
            ? [{
                etiqueta: 'Dar de baja', peligro: true,
                onClick: () => api.patch(`/sucursales/${s.id}`, { activo: false })
                  .then(cargar).catch((e) => setError(e.message)),
              }]
            : [{
                etiqueta: 'Reactivar',
                onClick: () => api.patch(`/sucursales/${s.id}`, { activo: true }).then(cargar),
              }]),
        ]}
      />

      {/* El fallo de carga lo pinta la lista (motivo en español + reintentar). */}

      {editar && (
        <ModalSucursal
          {...editar}
          onCerrar={() => setEditar(null)}
          onListo={() => { setEditar(null); cargar(); }}
        />
      )}

      {mapa && (
        <Modal titulo={mapa.nombre} ancho="max-w-lg" onCerrar={() => setMapa(null)}>
          <MapaSucursal sucursal={mapa} alto={260} />
        </Modal>
      )}
    </>
  );
}

const CAMPOS_DIRECCION = [
  ['calle', 'Calle', 'Avenida Industrial'],
  ['numero_exterior', 'Número exterior', '1200'],
  ['numero_interior', 'Número interior', 'A'],
  ['colonia', 'Colonia', 'Parque Industrial'],
  ['municipio', 'Municipio o alcaldía', 'Apodaca'],
  ['estado', 'Estado', 'Nuevo León'],
  ['codigo_postal', 'Código postal', '66600'],
  ['telefono', 'Teléfono', '8112345678'],
];

function ModalSucursal({ modo, sucursal, onCerrar, onListo }) {
  const crear = modo === 'crear';
  const [f, setF] = useState(() => ({
    nombre: sucursal?.nombre || '',
    direccion: sucursal?.direccion || '',
    latitud: sucursal?.latitud ?? '',
    longitud: sucursal?.longitud ?? '',
    zona_horaria: sucursal?.zona_horaria || '',
    ...Object.fromEntries(CAMPOS_DIRECCION.map(([k]) => [k, sucursal?.[k] || ''])),
  }));
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  // Las zonas y CUAL ES LA DE LA EMPRESA, que es la que se hereda si esta
  // se deja vacia. Sin ese dato, «La de la empresa» no dice cual.
  const [zonas, setZonas] = useState({ empresa: '', opciones: [] });
  useEffect(() => {
    api.get('/sucursales/zonas-horarias').then(setZonas).catch(() => {});
  }, []);
  const nombreDeZona = (clave) =>
    zonas.opciones.find((o) => o.clave === clave)?.nombre || clave;

  return (
    <ModalConfirmar
      titulo={crear ? 'Nueva sucursal' : `Editar ${sucursal.nombre}`}
      descripcion={crear
        ? 'Basta con el nombre. La dirección y las coordenadas se pueden completar después, en cualquier momento.'
        : null}
      extra={
        <div className="space-y-5">
          <Seccion titulo="La sucursal" columnas={1}>
            <CampoValidado etiqueta="Nombre" requerido valor={f.nombre}
                           validar={(v) => (v.trim() ? null : 'El nombre es obligatorio.')}>
              {(props) => <input {...props} value={f.nombre} onChange={set('nombre')}
                                 placeholder="Bodega Norte" />}
            </CampoValidado>
            {crear && (
              <CampoValidado etiqueta="Dirección" ayuda="Opcional; se puede completar después."
                             valor={f.direccion}>
                {(props) => <input {...props} value={f.direccion} onChange={set('direccion')} />}
              </CampoValidado>
            )}
          </Seccion>

          {!crear && (
            <>
              {/* `Seccion` reparte en DOS columnas solo a partir de `sm`. El
                  `grid-cols-2` que había aquí eran dos columnas también en un
                  teléfono de 360 px: ocho campos de dirección partidos por la
                  mitad. */}
              <Seccion titulo="Dirección"
                       descripcion="Sirve para que quien llega sepa a dónde ir, y para los documentos de compra.">
                {CAMPOS_DIRECCION.map(([k, etiqueta, ejemplo]) => (
                  <CampoValidado key={k} etiqueta={etiqueta} valor={f[k]}>
                    {(props) => <input {...props} value={f[k]} onChange={set(k)}
                                       placeholder={ejemplo} />}
                  </CampoValidado>
                ))}
              </Seccion>

              <Seccion titulo="La hora de esta sucursal" columnas={1}
                       descripcion="Con esta hora salen sus facturas y con esta se decide a qué mes pertenece cada venta. Solo hace falta cambiarla si esta sucursal está en otro huso que el resto de la empresa.">
                <CampoValidado etiqueta="Zona horaria" valor={f.zona_horaria}
                               ayuda={f.zona_horaria
                                 ? 'Manda sobre la de la empresa.'
                                 : `Se usa la de la empresa: ${nombreDeZona(zonas.empresa)}.`}>
                  {(props) => (
                    <select {...props} value={f.zona_horaria}
                            onChange={set('zona_horaria')}>
                      <option value="">
                        La misma que la empresa{zonas.empresa ? ` — ${nombreDeZona(zonas.empresa)}` : ''}
                      </option>
                      {zonas.opciones.map((o) => (
                        <option key={o.clave} value={o.clave}>{o.nombre}</option>
                      ))}
                    </select>
                  )}
                </CampoValidado>
              </Seccion>

              <Seccion titulo="Coordenadas"
                       descripcion="Las dos o ninguna: con una sola no se ubica nada. Se sacan de Google Maps con clic derecho sobre el punto.">
                {/* La regla del par se comprobaba DENTRO de onConfirmar, o sea
                    al pulsar Guardar y como banner rojo. Ahora la dice el campo
                    que falta, en cuanto sales de él. */}
                <CampoValidado etiqueta="Latitud" valor={f.latitud} validar={() => faltaPar(f)}>
                  {(props) => <input {...props} value={f.latitud} onChange={set('latitud')}
                                     className={`${props.className} font-mono`}
                                     placeholder="25.781944" />}
                </CampoValidado>
                <CampoValidado etiqueta="Longitud" valor={f.longitud} validar={() => faltaPar(f)}>
                  {(props) => <input {...props} value={f.longitud} onChange={set('longitud')}
                                     className={`${props.className} font-mono`}
                                     placeholder="-100.188611" />}
                </CampoValidado>
              </Seccion>

              {(tieneCoordenadas(f) || direccionLegible(f)) && (
                <MapaSucursal sucursal={{ ...f, nombre: f.nombre }} alto={160} />
              )}
            </>
          )}
        </div>
      }
      etiquetaConfirmar={crear ? 'Crear sucursal' : 'Guardar cambios'}
      onConfirmar={async () => {
        if (!f.nombre.trim()) throw new ErrorApi('El nombre es obligatorio.');
        if (crear) {
          await api.post('/sucursales', {
            nombre: f.nombre.trim(), direccion: f.direccion.trim() || null,
            zona_horaria: f.zona_horaria || null,
          });
          onListo();
          return;
        }

        const lat = String(f.latitud).trim();
        const lon = String(f.longitud).trim();
        // El campo ya lo avisa al salir de él; esto es la última defensa
        // para quien llegue al botón sin haber pasado por los dos.
        const par = faltaPar(f);
        if (par) throw new ErrorApi(par);

        const cuerpo = { nombre: f.nombre.trim() };
        // Vacía significa «hereda». Va como bandera y no como null
        // porque el guardado descarta los nulos —así distingue «no lo
        // toques» de «déjalo vacío»—, y sin esto una zona puesta por
        // error no se podría quitar nunca.
        if (f.zona_horaria) cuerpo.zona_horaria = f.zona_horaria;
        else cuerpo.heredar_zona_horaria = true;
        for (const [k] of CAMPOS_DIRECCION) {
          if (String(f[k]).trim()) cuerpo[k] = String(f[k]).trim();
        }
        if (lat) { cuerpo.latitud = lat; cuerpo.longitud = lon; }
        else if (tieneCoordenadas(sucursal)) cuerpo.quitar_coordenadas = true;

        await api.patch(`/sucursales/${sucursal.id}`, cuerpo);
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}

